<template>
  <section id="login-wrapper">
    <template v-if="!isAuthenticated">
      <div class="mb-15">
        <h1 class="title is-size-4 is-size-3-mobile has-text-color-7">
          {{ Home.ACCESS.toUpperCase() }}
        </h1>
        <p class="subtitle is-size-7 is-size-6-mobile has-text-color-7 px-10">
          {{ Home.Login.SUITE_USES_GUARDIAN_LOGIN }}
        </p>
      </div>

      <div>
        <b-loading :active="isLoading" />
        <iframe
          id="guardianLogin"
          ref="guardianLogin"
          :src="loginURL"
          title="Janela de login do Guardião"
          class="no-border"
        ></iframe>

        <div
          class="divider is-horizontal has-text-color-7 is-capitalized is-size-6 spacing-mx-10"
        >
          <span>{{ Home.OR }}</span>
        </div>

        <b-button
          id="modal-button"
          data-cy="DIGITAL_CERT_BUTTON"
          class="mt-10"
          :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
          outlined
          expanded
          :size="isMobile ? 'is-medium' : ''"
          :iconLeft="isMobile ? 'lock' : ''"
          @click="openModalDigitalCert"
        >
          <div class="is-flex is-align-items-center">
            <USBIcon class="is-hidden-touch" />
            <span class="has-custom-text-size">
              {{ Home.DIGITAL_CERT }}
            </span>
          </div>
        </b-button>
      </div>
    </template>

    <template v-else>
      <div class="content">
        <div class="has-text-centered custom-mtb-wrapper">
          <b-icon
            icon="check-circle"
            size="is-large"
            :type="isContrast ? 'is-white-dark' : 'is-green-dark'"
          />

          <p class="title is-6 mt-20">{{ Home.YOU_ARE_CONNECTED }}</p>
          <p>{{ Home.IF_YOU_WANT_TO_RETURN }}</p>

          <b-button
            :type="isContrast ? 'is-white-dark' : 'is-blue-oil'"
            tag="router-link"
            to="/app"
          >
            <span class="has-custom-text-size px-20">
              {{ Home.ACCESS_SYSTEM.toLocaleUpperCase() }}
            </span>
          </b-button>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import Cleave from "cleave.js";

import ModalDigitalCertificate from "@/views/App/ModalDigitalCertificate.vue";
import { General, Home } from "@/utils/constants";
import USBIcon from "@/components/USBIcon";
import { GUARDIAN_STATUS } from "@/utils/enumerate";

export default {
  name: "Login",
  components: {
    USBIcon
  },
  directives: {
    cleave: {
      bind(el, binding) {
        const input = el.querySelector("input");
        if (input) input._vCleave = new Cleave(input, binding.value);
      },
      unbind(el) {
        const input = el.querySelector("input");
        if (input) input._vCleave.destroy();
      }
    }
  },

  data() {
    return {
      General,
      Home,
      isLoading: false,
      CPF: "",
      CPFError: false,
      CPFRawValue: "",
      password: "",
      passwordError: false,
      masks: {
        cpf: {
          delimiters: [".", ".", "-"],
          blocks: [3, 3, 3, 2],
          numericOnly: true
        }
      },
      isMobile: false
    };
  },

  computed: {
    ...mapGetters({
      user: "getCurrentUser",
      isAuthenticated: "isAuthenticated",
      isContrast: "isContrast"
    }),

    loginURL() {
      return process.env.VUE_APP_GUARDIAN_LOGIN_URL + "&showAlert=false";
    }
  },

  watch: {
    CPFRawValue: {
      handler(value) {
        if (value.length === 11) {
          this.CPFError = false;
        }
      }
    },
    password: {
      handler(value) {
        if (value.length) {
          this.passwordError = false;
        }
      }
    }
  },

  mounted() {
    this.checkMobile();
  },

  created() {
    window.addEventListener("resize", this.checkMobile);
    window.addEventListener("message", this.checkGuardianMessage);
  },

  destroyed() {
    window.removeEventListener("resize", this.checkMobile);
    window.removeEventListener("message", this.checkGuardianMessage);
  },

  methods: {
    ...mapActions({
      authLogin: "authLogin",
      loginWithTokenGuardian: "authLoginGuardian",
      holidaysRequest: "holidaysRequest"
    }),
    ...mapMutations({
      setViewSector: "setViewSector",
      setCurrentTab: "setCurrentTabProcessPanel"
    }),

    validateData() {
      let isValidated = true;
      if (!this.CPF) {
        this.CPFError = true;
        isValidated = false;
      }
      if (!this.password) {
        this.passwordError = true;
        isValidated = false;
      }
      return isValidated;
    },

    async checkAuthenticated() {
      if (this.isAuthenticated) {
        await this.holidaysRequest();
        const pathDoc = localStorage.getItem("goDoc");

        if (pathDoc) {
          this.$router.push(pathDoc);
          localStorage.removeItem("goDoc");
        } else this.$router.push("/app");

        this.setViewSector("INDIVIDUAL");
        this.setCurrentTab("SECTOR");
      }
    },

    async login() {
      if (this.validateData()) {
        this.isLoading = true;
        await this.authLogin({
          username: this.CPFRawValue,
          password: this.password
        });
        await this.checkAuthenticated();
        this.isLoading = false;
      }
    },

    onInput(event) {
      this.CPFRawValue = event.target._vCleave.getRawValue();
      this.CPF = event.target._vCleave.getFormattedValue();
    },

    redirect(routePath) {
      this.$router.push(routePath);
    },

    openModalDigitalCert() {
      this.$buefy.modal.open({
        parent: this,
        component: ModalDigitalCertificate,
        props: {
          onClose: async () => {
            this.isLoading = true;
            await this.checkAuthenticated();
            this.isLoading = false;
          }
        },
        hasModalCard: true,
        trapFocus: true
      });
    },
    checkMobile() {
      if (window.innerWidth < 768) {
        this.isMobile = true;
      } else this.isMobile = false;
    },
    setGuardianToast(code = "GENERIC_ERROR") {
      this.snackToast.open({
        message:
          Home.Login.MESSAGES[code] ?? Home.Login.MESSAGES["GENERIC_ERROR"],
        type: GUARDIAN_STATUS[code] ?? GUARDIAN_STATUS["GENERIC_ERROR"],
        duration: 8000
      });
    },
    async resolveGuardianLogin(token) {
      if (token) {
        this.isLoading = true;
        const result = await this.loginWithTokenGuardian(token);
        this.isLoading = false;
        if (result) {
          this.$router.replace("/app");
        } else this.$refs.guardianLogin.src = this.$refs.guardianLogin.src + "";
      }
    },
    async checkGuardianMessage({ data, origin }) {
      const hostname = /^(?:https?:\/\/)?(?:www\.)?([^/]+)/.exec(
        this.loginURL
      )?.[0];
      if (origin === hostname) {
        if (typeof data?.p === "string") {
          const token = data.p;
          await this.resolveGuardianLogin(token);
        } else if (data?.message && data?.code) {
          this.setGuardianToast(data.code);
        }
        if (data?.height && this.$refs.guardianLogin) {
          this.$refs.guardianLogin.style.height = `${data.height}px`;
        }
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/theme.scss";
@import "@/assets/scss/variables.scss";

#guardianLogin {
  min-height: 296px;
}

#login-wrapper {
  display: flex;
  flex-direction: column;
  max-width: 400px;
  background-color: var(--color-1);
  border-radius: 5px;
  box-shadow: 0 2px 4px 0 $card-shadow-color1, 0 2px 8px 0 $card-shadow-color2;
  padding: 45px 50px;
  @media only screen and (max-width: $tablet-low) {
    min-width: 85vw;
    padding: 30px 20px;
    .button.is-medium span span {
      font-size: 1rem;
    }
  }
}

.has-custom-link:hover {
  text-decoration: underline;
}

.has-custom-text-size {
  font-size: 0.75rem;
  font-weight: 600;
}

.labeleffect {
  section {
    position: absolute;
    margin-left: 10px;
    margin-top: -10px;
    color: var(--dynamic-title-color);
    background-color: transparent;
    transition: 0.3s;
    height: 20px;
    width: 29px;
    z-index: 98;
    overflow: hidden;
    span {
      position: absolute;
      left: 0;
      font-size: 14px;
      z-index: 99;
    }
    header {
      position: absolute;
      top: 0;
      height: 11px;
      width: 30px;
      background-color: var(--app-background-light-color);
      z-index: 97;
    }
  }
}

.has-no-content {
  section {
    display: none;
  }
}

.custom-mtb-wrapper {
  margin: 49px 0;
  .icon {
    width: 6rem;
    height: 6rem;
  }
}
.divider::after,
.divider::before {
  background-color: $color-7 !important;
}

.no-border {
  border: 0;
}
</style>

<style lang="scss">
@import "@/assets/scss/variables.scss";
#login-wrapper {
  .icon.has-text-primary {
    color: $color-7 !important;
  }

  input {
    border-color: $color-7 !important;
  }

  .cpf-field {
    label {
      color: $color-8;
      &:before {
        background-color: $color-1;
      }
    }
  }
}
</style>
